/**
 * Synthesised notification sounds using the Web Audio API.
 * No audio files — everything is generated at runtime.
 *
 * Chimes (DESIGN.md §11):
 *  • attentionChime   — gentle two-tone nudge (countdown last minute)
 *  • successChime     — bright major-chord arpeggio + pad (redirect / daily goal)
 *  • dailyChime       — semantic alias of successChime (daily goal complete)
 *  • biteChime        — ~500ms upper-arpeggio + shimmer (bite 2+ checkpoint)
 *  • correctChime     — short 250ms bling (per-question correct)
 *  • incorrectChime   — gentle 400ms descent (per-question wrong)
 *  • masteryKeynote   — 3.4s four-phase signature (node mastered)
 *  • masteryChime     — alias of masteryKeynote
 *
 * Primitives:
 *  • playTone        — single oscillator with ADSR envelope
 *  • playAppleVoice  — filtered triangle voice with ADSR (chord voicing)
 *  • playAppleBell   — additive bell with inharmonic partials + exponential decay
 *
 * Mute gating (Decision #2 from /plan-eng-review, 2026-04-18):
 * Every chime checks the module-level `_soundEnabled` flag before producing
 * audio. Callers don't thread an `enabled` prop — muted chimes are silent
 * no-ops. SoundContext mutates the flag via `setSoundEnabled()`; chimes are
 * called directly as before.
 */

type OscType = 'sine' | 'square' | 'sawtooth' | 'triangle';

// ── Module-singleton mute gate ────────────────────────────────────────

let _soundEnabled = true;

/** Read the current mute state. Used by tests and consumers that need to
 *  branch on it (e.g. suppressing a visual cue that pairs with a sound). */
export function isSoundEnabled(): boolean {
  return _soundEnabled;
}

/** Mutate the mute state. SoundContext calls this on mount (seeding from
 *  localStorage) and on every toggle. */
export function setSoundEnabled(enabled: boolean): void {
  _soundEnabled = enabled;
}

// ── Audio context helper ──────────────────────────────────────────────

function getAudioContext(): AudioContext | null {
  try {
    return new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext)();
  } catch {
    return null;
  }
}

// ── Primitive: playTone ───────────────────────────────────────────────

interface ToneOptions {
  ctx: AudioContext;
  frequency: number;
  startTime: number;
  duration: number;
  volume?: number;
  type?: OscType;
  fadeIn?: number;
  fadeOut?: number;
}

function playTone({
  ctx,
  frequency,
  startTime,
  duration,
  volume = 0.12,
  type = 'sine',
  fadeIn = 0.04,
  fadeOut = 0.15,
}: ToneOptions): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(frequency, startTime);

  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(volume, startTime + fadeIn);
  gain.gain.setValueAtTime(volume, startTime + duration - fadeOut);
  gain.gain.linearRampToValueAtTime(0, startTime + duration);

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.start(startTime);
  osc.stop(startTime + duration);
}

// ── Primitive: playAppleVoice (DESIGN.md §11) ─────────────────────────
// Filtered triangle voice with ADSR envelope + lowpass colouring.
// Used for the mastery keynote's G6/9 chord voicing.

interface AppleVoiceOptions {
  ctx: AudioContext;
  freq: number;
  start: number;
  dur: number;
  vol?: number;
  /** Cents of detune; a trio of voices at (-4, 0, +4) gives analog-style chorus. */
  detune?: number;
  /** Attack time in seconds (DESIGN.md specifies a fast 180ms arrival for Phase 2). */
  attack?: number;
  /** Release time in seconds. */
  release?: number;
  /** Lowpass filter frequency — DESIGN.md: 2200-3600 Hz (darker on bass). */
  filterHz?: number;
}

function playAppleVoice({
  ctx,
  freq,
  start,
  dur,
  vol = 0.05,
  detune = 0,
  attack = 0.18,
  release = 0.6,
  filterHz = 3200,
}: AppleVoiceOptions): void {
  const osc = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();

  osc.type = 'triangle';
  osc.frequency.setValueAtTime(freq, start);
  osc.detune.setValueAtTime(detune, start);

  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(filterHz, start);
  filter.Q.setValueAtTime(0.7, start);

  const sustainEnd = start + dur - release;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(vol, start + attack);
  gain.gain.setValueAtTime(vol, Math.max(start + attack, sustainEnd));
  gain.gain.linearRampToValueAtTime(0, start + dur);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);

  osc.start(start);
  osc.stop(start + dur + 0.02);
}

// ── Primitive: playAppleBell (DESIGN.md §11) ──────────────────────────
// Additive bell: fundamental + 2x + 3x + 4.1x (inharmonic) partials.
// Each partial decays exponentially; weighted so the fundamental dominates.

interface AppleBellOptions {
  ctx: AudioContext;
  freq: number;
  start: number;
  dur: number;
  vol?: number;
}

function playAppleBell({
  ctx,
  freq,
  start,
  dur,
  vol = 0.06,
}: AppleBellOptions): void {
  const partials = [
    { ratio: 1.0, amp: 1.0 },
    { ratio: 2.0, amp: 0.45 },
    { ratio: 3.0, amp: 0.22 },
    { ratio: 4.1, amp: 0.14 },
  ];

  for (const { ratio, amp } of partials) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq * ratio, start);

    const peak = vol * amp;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(peak, start + 0.008);
    // Exponential decay. setTargetAtTime approaches 0; we stop the osc
    // before the tail becomes audible.
    gain.gain.setTargetAtTime(0, start + 0.02, dur / 3);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(start);
    osc.stop(start + dur);
  }
}

// ── Attention chime ───────────────────────────────────────────────────
// Two ascending tones: C5 → E5. Gentle, non-intrusive.

export function playAttentionChime(): void {
  if (!_soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const t = ctx.currentTime;

  playTone({
    ctx,
    frequency: 523.25,
    startTime: t,
    duration: 0.3,
    volume: 0.1,
  }); // C5
  playTone({
    ctx,
    frequency: 659.25,
    startTime: t + 0.18,
    duration: 0.4,
    volume: 0.12,
  }); // E5

  setTimeout(() => ctx.close().catch(() => {}), 1500);
}

// ── Success chime (daily goal) ────────────────────────────────────────
// Bright major-chord arpeggio (C5 → E5 → G5 → C6) over a warm sustained pad.

export function playSuccessChime(): void {
  if (!_soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const t = ctx.currentTime;

  // Warm pad underneath — soft triangle wave on C4, wide and sustained
  playTone({
    ctx,
    frequency: 261.63,
    startTime: t,
    duration: 1.4,
    volume: 0.04,
    type: 'triangle',
    fadeIn: 0.1,
    fadeOut: 0.5,
  });

  // Second pad voice — E4 for warmth
  playTone({
    ctx,
    frequency: 329.63,
    startTime: t + 0.05,
    duration: 1.2,
    volume: 0.03,
    type: 'triangle',
    fadeIn: 0.1,
    fadeOut: 0.4,
  });

  const arpeggio = [
    { freq: 523.25, delay: 0.0, vol: 0.08, dur: 0.35 },
    { freq: 659.25, delay: 0.12, vol: 0.1, dur: 0.32 },
    { freq: 783.99, delay: 0.24, vol: 0.11, dur: 0.3 },
    { freq: 1046.5, delay: 0.38, vol: 0.13, dur: 0.5 },
  ];

  for (const note of arpeggio) {
    playTone({
      ctx,
      frequency: note.freq,
      startTime: t + note.delay,
      duration: note.dur,
      volume: note.vol,
      fadeIn: 0.02,
      fadeOut: 0.12,
    });
  }

  playTone({
    ctx,
    frequency: 2093.0,
    startTime: t + 0.42,
    duration: 0.6,
    volume: 0.025,
    type: 'sine',
    fadeIn: 0.03,
    fadeOut: 0.3,
  });

  setTimeout(() => ctx.close().catch(() => {}), 2500);
}

/** Semantic alias — the chosen daily goal or best day reached. Same melody as successChime. */
export const playDailyChime = playSuccessChime;

// ── Bite chime (stackable-bites checkpoint, DESIGN.md §11) ────────────
// Lighter ~500ms variant for bite 2+ inside one sitting. Same C-major
// family as playDailyChime (so it still reads as "daily-flavoured" sparkle),
// but compressed to the upper arpeggio and stripped of the warm pad — the
// pad is what makes the daily chime feel ceremonial, and bite 2+ should
// feel like a checkpoint, not another goal completion. Preserves the
// four-tier sound hierarchy by keeping a clear gap between bite-1's full
// daily celebration and the per-question correct/wrong chimes.

export function playBiteChime(): void {
  if (!_soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const t = ctx.currentTime;

  const arpeggio = [
    { freq: 659.25, delay: 0.0, vol: 0.08, dur: 0.2 }, // E5
    { freq: 783.99, delay: 0.07, vol: 0.1, dur: 0.22 }, // G5
    { freq: 1046.5, delay: 0.16, vol: 0.11, dur: 0.32 }, // C6
  ];

  for (const note of arpeggio) {
    playTone({
      ctx,
      frequency: note.freq,
      startTime: t + note.delay,
      duration: note.dur,
      volume: note.vol,
      fadeIn: 0.015,
      fadeOut: 0.1,
    });
  }

  // C7 shimmer — echoes the high sparkle from playDailyChime so the two
  // chimes feel like the same family.
  playTone({
    ctx,
    frequency: 2093.0,
    startTime: t + 0.2,
    duration: 0.28,
    volume: 0.022,
    type: 'sine',
    fadeIn: 0.02,
    fadeOut: 0.15,
  });

  setTimeout(() => ctx.close().catch(() => {}), 700);
}

// ── Correct chime (per-question correct, DESIGN.md §11) ───────────────
// Short ~250ms two-note bling: G5 sine → C6 sine + tiny C7 shimmer.

export function playCorrectChime(): void {
  if (!_soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const t = ctx.currentTime;

  playTone({
    ctx,
    frequency: 783.99, // G5
    startTime: t,
    duration: 0.14,
    volume: 0.09,
    fadeIn: 0.01,
    fadeOut: 0.06,
  });
  playTone({
    ctx,
    frequency: 1046.5, // C6
    startTime: t + 0.08,
    duration: 0.18,
    volume: 0.1,
    fadeIn: 0.01,
    fadeOut: 0.08,
  });
  playTone({
    ctx,
    frequency: 2093.0, // C7 shimmer
    startTime: t + 0.1,
    duration: 0.15,
    volume: 0.025,
    fadeIn: 0.01,
    fadeOut: 0.08,
  });

  setTimeout(() => ctx.close().catch(() => {}), 500);
}

// ── Incorrect chime (per-question wrong, DESIGN.md §11) ───────────────
// Gentle 3-note descent: E5 → C5 → A4 at low volume. Not punitive.

export function playIncorrectChime(): void {
  if (!_soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const t = ctx.currentTime;

  const descent = [
    { freq: 659.25, delay: 0.0 }, // E5
    { freq: 523.25, delay: 0.12 }, // C5
    { freq: 440.0, delay: 0.24 }, // A4
  ];

  for (const note of descent) {
    playTone({
      ctx,
      frequency: note.freq,
      startTime: t + note.delay,
      duration: 0.18,
      volume: 0.05,
      fadeIn: 0.015,
      fadeOut: 0.1,
    });
  }

  setTimeout(() => ctx.close().catch(() => {}), 700);
}

// ── Mastery keynote (node mastered, DESIGN.md §11) ────────────────────
// Four-phase ~3.4s signature sound. Apple product-event taste.
//
// Phase 1 (0-400ms)    Rising sparkle bells  D5 → G5 → B5 (80ms stagger)
// Phase 2 (250-3400ms) G6/9 chord arrival — 6-note voicing, 3 detuned voices each
// Phase 3 (800-2200ms) Shimmer cascade bells E6, G6, B6, D7 (100ms stagger)
// Phase 4 (1350-3400ms) Plagal IV→I resolve: C5 (brief) → G4 (held)

export function playMasteryKeynote(): void {
  if (!_soundEnabled) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const t = ctx.currentTime;

  // Phase 1 — rising sparkle bells (0-400ms)
  const phase1 = [
    { freq: 587.33, delay: 0.0 }, // D5
    { freq: 783.99, delay: 0.08 }, // G5
    { freq: 987.77, delay: 0.16 }, // B5
  ];
  for (const b of phase1) {
    playAppleBell({
      ctx,
      freq: b.freq,
      start: t + b.delay,
      dur: 0.6,
      vol: 0.055,
    });
  }

  // Phase 2 — G6/9 chord arrival at 250ms, held to ~3.4s
  // Voicing: G2, G3, D4, E4, A4, B4
  // 3 detuned triangle voices per note (-4¢, 0¢, +4¢)
  // Filter brighter on top notes (2200-3600 Hz).
  const chordStart = t + 0.25;
  const chordDur = 3.15;
  const chord = [
    { freq: 98.0, filterHz: 2200 }, // G2
    { freq: 196.0, filterHz: 2400 }, // G3
    { freq: 293.66, filterHz: 2800 }, // D4
    { freq: 329.63, filterHz: 3000 }, // E4
    { freq: 440.0, filterHz: 3300 }, // A4
    { freq: 493.88, filterHz: 3600 }, // B4
  ];
  for (const note of chord) {
    for (const detune of [-4, 0, 4]) {
      playAppleVoice({
        ctx,
        freq: note.freq,
        start: chordStart,
        dur: chordDur,
        vol: 0.022,
        detune,
        attack: 0.18,
        release: 0.8,
        filterHz: note.filterHz,
      });
    }
  }

  // Phase 3 — shimmer cascade (800-2200ms)
  const phase3 = [
    { freq: 1318.51, delay: 0.8 }, // E6
    { freq: 1567.98, delay: 0.9 }, // G6
    { freq: 1975.53, delay: 1.0 }, // B6
    { freq: 2349.32, delay: 1.1 }, // D7
  ];
  for (const s of phase3) {
    playAppleBell({
      ctx,
      freq: s.freq,
      start: t + s.delay,
      dur: 1.2,
      vol: 0.03,
    });
  }

  // Phase 4 — Plagal IV→I resolve (1350-3400ms)
  // C5 (IV, brief) → G4 (I, held warm)
  playAppleVoice({
    ctx,
    freq: 523.25,
    start: t + 1.35,
    dur: 0.6,
    vol: 0.05,
    attack: 0.08,
    release: 0.3,
    filterHz: 2800,
  });
  playAppleVoice({
    ctx,
    freq: 392.0, // G4
    start: t + 1.9,
    dur: 1.5,
    vol: 0.055,
    attack: 0.15,
    release: 0.9,
    filterHz: 2600,
  });

  setTimeout(() => ctx.close().catch(() => {}), 3600);
}

/** Alias — used in callsites that prefer the shorter name. */
export const playMasteryChime = playMasteryKeynote;
